import { useState, useEffect, useRef, useCallback } from 'react';
import { EventSourcePolyfill } from 'event-source-polyfill';
import { API_URL } from '@/config';
import storage from '@/utils/storage';

export const useUserCredits = (enabled: boolean) => {
  const [userCredits, setUserCredits] = useState<any>({});
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimerRef = useRef<NodeJS.Timeout | null>(null);
  const previousAccessTokenRef = useRef<string | null>(null);

  const teamData = storage?.getTeam();
  const xTeamIdFromLocalStorage = teamData ? teamData._id : null;
  const xTeamId = xTeamIdFromLocalStorage;

  const establishSSEConnection = useCallback(
    (accessToken: string) => {
      if (!enabled || !xTeamId || !accessToken) return;

      // Close existing connection before establishing new one
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }

      const url = `${API_URL}/v1/credit/stream`;
      const newEventSource = new EventSourcePolyfill(url, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'X-Team': xTeamId,
        },
        withCredentials: false,
      });
      eventSourceRef.current = newEventSource;

      newEventSource.onopen = () => {
        if (reconnectTimerRef.current) {
          clearTimeout(reconnectTimerRef.current);
          reconnectTimerRef.current = null;
        }
      };

      newEventSource.onmessage = (event) => {
        const data = JSON.parse(event.data);
        setUserCredits(data);
      };

      newEventSource.onerror = () => {
        newEventSource.close();
        reconnectTimerRef.current = setTimeout(() => {
          const currentToken = storage.getToken();
          if (currentToken) {
            establishSSEConnection(currentToken);
          }
        }, 5000);
      };
    },
    [enabled, xTeamId]
  );

  useEffect(() => {
    const access_token = storage.getToken();

    // Check if access token has changed
    if (
      previousAccessTokenRef.current !== null &&
      previousAccessTokenRef.current !== access_token
    ) {
      // Access token changed, close current connection and establish new one
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }

      // Establish new connection with updated token if enabled
      if (enabled && access_token && xTeamId) {
        establishSSEConnection(access_token);
      }
    } else if (enabled && !eventSourceRef.current && access_token && xTeamId) {
      // Initial connection or reconnection needed
      establishSSEConnection(access_token);
    } else if (!enabled) {
      // Disabled, close any existing connection
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    }

    // Update the previous access token reference
    previousAccessTokenRef.current = access_token;

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    };
  }, [enabled, xTeamId, establishSSEConnection]);

  const terminateSSEConnection = () => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  };

  return {
    userCredits,
    establishSSEConnection: () => establishSSEConnection(storage.getToken() || ''),
    terminateSSEConnection,
  };
};
